import {
  createFileRoute,
  Link,
  type MakeRouteMatchUnion,
} from "@tanstack/react-router";
import {
  Loader,
  Alert,
  AppError,
  EmptyState,
  Menu,
  MenuListItem,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Tag,
  toast,
} from "@bfc-software/pallet";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  AuthContext,
  AuthUtils,
  logger,
  useAssertedTenancyContext,
} from "@bfc-software/app-toolbox";
import {
  useTaskPlannerApi,
  useTaskPlannerFetchClient,
} from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

import {
  PPT_OPTIMIZATION_KIND,
  type TaskPlanDetail,
  type ActivationError,
  type ActivationView,
} from "../-components/types";
import { PlanSummary } from "../-components/plan-summary";
import { customerNamesOf, zoneLabelsOf } from "../-data/labels";
import {
  ACTIVATION_SWEEP_KEY,
  isConflict,
  useUnassignTasks,
} from "../-data/use-unassign-tasks";
import sharedStyles from "../-components/shared.module.css";

type TaskListResponse =
  TaskPlannerSchemaTypes.components["schemas"]["TaskListResponse"];
type TaskSummary = TaskPlannerSchemaTypes.components["schemas"]["TaskSummary"];
type PlanError = TaskPlannerSchemaTypes.components["schemas"]["PlanError"];
type TaskActivationSummary =
  TaskPlannerSchemaTypes.components["schemas"]["TaskActivationSummary"];

/**
 * The assignment state shared by every task row.
 *
 * A plan's task activations are fetched once for the whole page (keyed by the
 * latest plan_activation), so the rows share one loading/error result and a
 * lookup map rather than each issuing its own request.
 */
type AssignmentLookup =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; byTaskId: Map<string, TaskActivationSummary> };

type State =
  | { kind: "not-loaded" }
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "loaded"; plan: TaskPlanDetail; tasks: TaskSummary[] };

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/task-plans/$task-plan/",
)({
  staticData: {
    crumb: (match: MakeRouteMatchUnion, t: TFunction) => {
      const params = match.params as {
        lang: string;
        org: string;
        location: string;
        "task-plan": string;
      };

      const base = {
        lang: params.lang,
        org: params.org,
        location: params.location,
      };

      return [
        {
          label: t("page.taskPlans.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/task-plans",
          params: base,
        },
        {
          label: t("page.taskPlanDetail.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan",
          params: { ...base, "task-plan": params["task-plan"] },
        },
      ];
    },
  },
  component: RouteComponent,
});

/**
 * Picks the live activation row for a task.
 *
 * The activation listing returns every task_activation row, including ones
 * retired by the JIT supersede lifecycle. The current assignment is the row
 * that has not been superseded; in the MVP there is only ever one.
 */
function currentActivation(
  activations: TaskActivationSummary[],
): TaskActivationSummary | undefined {
  return activations.find((a) => a.superseded_at == null) ?? activations[0];
}

/**
 * The one place "unassignable" is decided: the live activation is `assigned`
 * and not yet claimed (the API 409s for anything else).
 */
function isUnassignable(
  row: TaskActivationSummary | undefined,
): row is TaskActivationSummary {
  return row?.status === "assigned" && row.claimed_at == null;
}

/** What a row needs to offer Unassign: the press handler and whether a write is in flight. */
type UnassignAction = {
  onUnassign: (taskId: string) => void;
  isPending: boolean;
};

/**
 * Folds a flat list of task activations (the whole plan activation) into a
 * task.id -> current activation lookup, applying the supersede rule per task.
 */
function buildAssignmentMap(
  activations: TaskActivationSummary[],
): Map<string, TaskActivationSummary> {
  const grouped = new Map<string, TaskActivationSummary[]>();
  for (const activation of activations) {
    /*
      `task_id` on this projection is task_activation.task_id_internal, despite
      the shorter name the summary shape gives it - and it is *not* the
      `task_internal_id` sitting beside it on the same shape, which is the
      warehouse-scoped number, the same one `TaskSummary` now spells out too.

      It is the ULID of a task on the plan that was activated - never an origin
      task on a merged plan's ancestor, whatever the column's "origin task"
      comment in the schema suggests. The activation writes one row per task it
      loaded through the activated plan (assignment/coordinator.go:
      `TaskIDInternal: result.task.task.ID`), and lineage lives in its own table.
      So keying this map by `TaskSummary.id` is sound for derived plans too.
    */
    const rows = grouped.get(activation.task_id) ?? [];
    rows.push(activation);
    grouped.set(activation.task_id, rows);
  }

  const result = new Map<string, TaskActivationSummary>();
  for (const [taskId, rows] of grouped) {
    const current = currentActivation(rows);
    if (current) {
      result.set(taskId, current);
    }
  }
  return result;
}

/**
 * Renders the selector assigned to a task.
 *
 * Assignments come from the page-wide activation lookup keyed by task.id. When
 * the plan has not been activated or the warehouse has no seeded selectors there
 * is no assignment, so we fall back to an "Unassigned" label.
 *
 * The cell says `external_user_id` - the holder's AAA user id, which is the id a
 * human here can trace to a person - and keeps the `selector_profile` ULID on the
 * `title`, where it is available to copy without occupying the one column that is
 * scanned for a name. The ULID is also the fallback: the spec has the two null
 * together, so an assigned row always carries both, but a blank cell would read
 * as missing data rather than as a row we could not name.
 */
function AssignedValue({
  assignments,
  taskId,
  t,
}: {
  assignments: AssignmentLookup;
  taskId: string;
  t: TFunction;
}) {
  if (assignments.kind === "loading") {
    return <Loader type="inline" />;
  }

  if (assignments.kind === "error") {
    return (
      <span
        className={sharedStyles.assignmentMuted}
        data-test-role="task-assignment-error"
      >
        {t("page.taskPlanDetail.assignment.loadFailed")}
      </span>
    );
  }

  const row = assignments.byTaskId.get(taskId);
  const selectorId = row?.selector_id;

  if (!selectorId) {
    return (
      <span
        className={sharedStyles.assignmentMuted}
        data-test-role="task-unassigned"
      >
        {t("page.taskPlanDetail.assignment.unassigned")}
      </span>
    );
  }

  return (
    <span
      data-test-role="task-selector-id"
      className={sharedStyles.truncatedId}
      title={selectorId}
    >
      {row?.external_user_id ?? selectorId}
    </span>
  );
}

/**
 * The row's actions menu. Unassign is its only action for now, so the menu is
 * offered only on rows it applies to; once a second action lands, render it on
 * every row and disable the items that do not apply.
 */
function TaskActions({
  assignments,
  taskId,
  t,
  unassign,
}: {
  assignments: AssignmentLookup;
  taskId: string;
  t: TFunction;
  unassign: UnassignAction;
}) {
  if (assignments.kind !== "ready") {
    return null;
  }

  if (!isUnassignable(assignments.byTaskId.get(taskId))) {
    return null;
  }

  return (
    <Menu
      trigger={{
        kind: "icon-only",
        variant: "tertiary",
        icon: "Menu",
        label: t("page.taskPlanDetail.rowMenu.ariaLabel"),
      }}
    >
      <MenuListItem
        isDisabled={unassign.isPending}
        onAction={() => unassign.onUnassign(taskId)}
      >
        {t("page.taskPlanDetail.unassign.action")}
      </MenuListItem>
    </Menu>
  );
}

function TaskRow({
  task,
  assignments,
  t,
  unassign,
}: {
  task: TaskSummary;
  assignments: AssignmentLookup;
  t: TFunction;
  unassign: UnassignAction;
}) {
  const { org, location, lang, "task-plan": planId } = Route.useParams();

  const palletCountMetric = task.optimization_metrics.find(
    (metric) => metric.kind === PPT_OPTIMIZATION_KIND,
  );
  const palletCount = palletCountMetric?.realized_pallets_per_task ?? 1;
  const potentialCount = palletCountMetric?.effective_pallets_per_task ?? 1;

  return (
    <TableRow key={task.id} data-test-role="task-row">
      <TableCell>{task.sort_order}</TableCell>
      <TableCell>
        <Link
          to="/$lang/orgs/$org/internal/whs/$location/task-plans/$task-plan/$task"
          params={{
            "task-plan": planId,
            task: String(task.task_internal_id),
            org,
            location,
            lang,
          }}
          className={sharedStyles.truncatedId}
          title={task.id}
        >
          {/*
            The warehouse-scoped number, which is what the link's own `task`
            param already is and the only one of a task's two ids a human reads
            out. The ULID stays on the `title` for anyone who needs to copy it.
          */}
          {task.task_internal_id}
        </Link>
      </TableCell>
      <TableCell>{task.task_type}</TableCell>
      <TableCell>
        <Tag
          label={task.sub_type}
          display={{
            tone: task.sub_type === "BULK" ? "warning" : "info",
          }}
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-4" data-test-role="task-row-zones">
          {zoneLabelsOf(task.zones).map((label) => (
            <Tag key={label} label={label} display={{ tone: "info" }} />
          ))}
        </div>
      </TableCell>
      <TableCell>
        <span data-test-role="task-row-customers">
          {customerNamesOf(task.customers ?? []).join(", ")}
        </span>
      </TableCell>
      <TableCell>{task.detail_count}</TableCell>
      <TableCell data-test-role="task-pallet-count-cell">
        <div className={sharedStyles.metricCellContainer}>
          <span
            className={sharedStyles.metricCellMainValue}
            data-test-role="task-realized-pallets"
          >
            {palletCount}
          </span>
          {palletCount !== potentialCount ? (
            <span
              data-test-role="task-potential-pallets"
              className={sharedStyles.metricCellAuxValue}
            >
              ({potentialCount})
            </span>
          ) : (
            <></>
          )}
        </div>
      </TableCell>
      <TableCell>{task.total_quantity}</TableCell>
      <TableCell data-test-role="task-assignment-cell">
        <AssignedValue assignments={assignments} taskId={task.id} t={t} />
      </TableCell>
      <TableCell className="text-right">
        <TaskActions
          assignments={assignments}
          taskId={task.id}
          t={t}
          unassign={unassign}
        />
      </TableCell>
    </TableRow>
  );
}

function TasksTable({
  children,
  t,
}: {
  children?: React.ReactNode;
  t: TFunction;
}) {
  return (
    <div className="overflow-x-auto">
      <Table
        aria-label={t("page.taskPlanDetail.table.ariaLabel")}
        isResizable={false}
      >
        <TableHead>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.seq")}
          </TableHeaderCell>
          <TableHeaderCell isRowHeader={true}>
            {t("page.taskPlanDetail.table.task")}
          </TableHeaderCell>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.type")}
          </TableHeaderCell>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.subType")}
          </TableHeaderCell>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.zones")}
          </TableHeaderCell>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.customers")}
          </TableHeaderCell>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.picks")}
          </TableHeaderCell>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.palletCount")}
          </TableHeaderCell>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.quantity")}
          </TableHeaderCell>
          <TableHeaderCell>
            {t("page.taskPlanDetail.table.assigned")}
          </TableHeaderCell>
          <TableHeaderCell>
            <span aria-label={t("page.taskPlanDetail.table.actions")} />
          </TableHeaderCell>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </div>
  );
}

function assertNever(x: never): never {
  throw new Error(`Unhandled case: ${JSON.stringify(x)}`);
}

/**
 * Renders a single algorithm error/warning attached to a task plan.
 *
 * The backend supplies a machine-readable `error_code` plus a human-readable
 * `message`. We look up a localized title/help by code under
 * `page.taskPlanDetail.errors.<CODE>`, falling back to the backend `message`
 * when no dictionary entry exists, so unknown codes still render usefully.
 */
function PlanErrorAlert({
  planError,
  t,
}: {
  planError: PlanError;
  t: TFunction;
}) {
  const code = planError.error_code;

  const title = t(`page.taskPlanDetail.errors.${code}.title`, {
    defaultValue: planError.message,
  });
  const help = t(`page.taskPlanDetail.errors.${code}.help`, {
    defaultValue: "",
  });

  const reference =
    planError.line_number != null
      ? t("page.taskPlanDetail.errors.referenceWithLine", {
          code,
          line: planError.line_number,
          defaultValue: `${code} · ${planError.line_number}`,
        })
      : code;

  return (
    <Alert
      data-test-role="plan-error"
      severity={planError.severity}
      title={title}
      reference={reference}
    >
      {help || null}
    </Alert>
  );
}

function PlanErrors({ errors, t }: { errors: PlanError[]; t: TFunction }) {
  if (errors.length < 1) {
    return null;
  }

  return (
    <div data-test-role="plan-errors" className={sharedStyles.planDetailErrors}>
      {errors.map((planError, index) => (
        <PlanErrorAlert key={index} planError={planError} t={t} />
      ))}
    </div>
  );
}

/**
 * Renders the errors recorded against a failed plan activation.
 *
 * Unlike plan (algorithm) errors these carry a `field_path` rather than a line
 * number, and have no localized dictionary, so we surface the backend `message`
 * directly and use the code (plus field path when present) as the reference.
 */
function ActivationErrors({ errors }: { errors: ActivationError[] }) {
  if (errors.length < 1) {
    return null;
  }

  return (
    <div
      data-test-role="activation-errors"
      className={sharedStyles.planDetailErrors}
    >
      {errors.map((error, index) => (
        <Alert
          key={index}
          data-test-role="activation-error"
          severity={error.severity}
          title={error.message}
          reference={
            error.field_path
              ? `${error.error_code} (${error.field_path})`
              : error.error_code
          }
        >
          {null}
        </Alert>
      ))}
    </div>
  );
}

function TaskPlanDetailPage({
  state,
  orgId,
  activation,
  assignments,
  unassign,
  t,
  lang,
  location,
}: {
  state: State;
  orgId: string;
  activation: ActivationView;
  assignments: AssignmentLookup;
  unassign: UnassignAction;
  t: TFunction;
  lang: string;
  location: string;
}) {
  logger.trace(`Rendering task-plan detail route state '${state.kind}'`);

  switch (state.kind) {
    case "not-loaded":
    case "loading":
      return (
        <div
          data-test-role="task-plan-detail-loading"
          className={sharedStyles.loadingContainer}
        >
          <Loader type="page" />
        </div>
      );
    case "loaded": {
      const errors = state.plan.errors ?? [];

      return (
        <div className={sharedStyles.planDetailLayout}>
          <PlanSummary
            plan={state.plan}
            orgId={orgId}
            activation={activation}
            t={t}
            lang={lang}
            location={location}
          />
          <div className={sharedStyles.planDetailContent}>
            <PlanErrors errors={errors} t={t} />
            <ActivationErrors
              errors={activation.kind === "present" ? activation.errors : []}
            />
            <div className={sharedStyles.planDetailMain}>
              <TasksTable t={t}>
                {state.tasks.length < 1 ? (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <EmptyState heading={t("page.taskPlanDetail.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  state.tasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      assignments={assignments}
                      t={t}
                      unassign={unassign}
                    />
                  ))
                )}
              </TasksTable>
            </div>
          </div>
        </div>
      );
    }
    case "failed":
      return (
        <TasksTable t={t}>
          <TableRow>
            <TableCell colSpan={11}>
              <div className={sharedStyles.errorContainer}>
                <AppError
                  description={t("page.taskPlanDetail.failed")}
                  resolution={t("page.taskPlanDetail.failureSupport")}
                />
              </div>
            </TableCell>
          </TableRow>
        </TasksTable>
      );
    default:
      return assertNever(state);
  }
}

function nextState<T>(
  state: State,
  planQuery: UseQueryResult<TaskPlanDetail, T>,
  tasksQuery: UseQueryResult<TaskListResponse, T>,
): State | undefined {
  switch (state.kind) {
    case "not-loaded":
      if (planQuery.isFetching || tasksQuery.isFetching) {
        return { kind: "loading" };
      }
      break;
    case "loading":
      if (planQuery.isError || tasksQuery.isError) {
        return { kind: "failed" };
      }
      if (planQuery.isSuccess && tasksQuery.isSuccess) {
        const plan = planQuery.data;
        const tasks = tasksQuery.data.tasks;
        return { kind: "loaded", plan, tasks };
      }
      break;
  }

  return void 0;
}

export function RouteComponent() {
  const { location, lang, "task-plan": planId } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const [state, setState] = useState<State>({ kind: "not-loaded" });

  const client = useTaskPlannerApi();
  const raw = useTaskPlannerFetchClient();

  const { orgId, warehouseId } = useAssertedTenancyContext();
  const tenantId = AuthUtils.getTenantId(authState, orgId, warehouseId);

  const planQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}",
    { params: { path: { orgId, id: planId } } },
    { enabled: !!tenantId },
  );

  const tasksQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/tasks",
    { params: { path: { orgId, id: planId } } },
    { enabled: !!tenantId },
  );

  /*
    The plan's current activation, straight off the plan read.

    `TaskPlanDetailResponse.activation` is the rollup of the most recent one, and
    `null` there is what "never activated" looks like - so there is nothing to
    discover by listing `/task-plans/{id}/activations`, which is what this used to
    do. That listing was a second read for an id and a status already in hand, and
    when it did not answer, every completed plan rendered as never activated with
    an "Unassigned" selector on every row.

    Its detail is still read below, because the rollup carries no error rows.
  */
  const latestActivation = planQuery.data?.activation ?? undefined;

  const activationDetailQuery = client.useQuery(
    "get",
    "/{orgId}/task-plans/{id}/activations/{activationId}",
    {
      params: {
        path: { orgId, id: planId, activationId: latestActivation?.id ?? "" },
      },
    },
    { enabled: !!tenantId && !!latestActivation },
  );

  const activation = useMemo<ActivationView>(() => {
    if (planQuery.isLoading) {
      return { kind: "loading" };
    }
    if (!latestActivation) {
      return { kind: "none" };
    }
    return {
      kind: "present",
      status: latestActivation.status,
      errors: activationDetailQuery.data?.errors ?? [],
    };
  }, [planQuery.isLoading, latestActivation, activationDetailQuery.data]);

  // Fetch every task activation for the latest plan activation in one paginated
  // sweep (replacing the previous per-row request, which was N+1). Rows read
  // their assignment from the resulting task.id -> activation map.
  const assignmentsQuery = useQuery({
    queryKey: [ACTIVATION_SWEEP_KEY, orgId, planId, latestActivation?.id],
    enabled: !!tenantId && !!latestActivation,
    queryFn: async () => {
      const activationId = latestActivation!.id;
      const rows: TaskActivationSummary[] = [];
      let cursor: string | undefined;

      do {
        const { data, error } = await raw.GET("/{orgId}/task-activations", {
          params: {
            path: { orgId },
            query: {
              // Scoped by the activation batch rather than by plan: a plan's
              // earlier activations are still on file, and only the latest
              // batch's rows describe the assignments shown here.
              planActivationId: activationId,
              // Required by the listing: the warehouse-scoped identifiers it
              // filters on are unique only within one warehouse, so there is no
              // org-wide read. `location` is the AAA locid the route is keyed
              // on, which is exactly what this param takes.
              warehouseExtId: warehouseId,
              // Supersede history included -- buildAssignmentMap applies the
              // live-row rule itself, per task.
              live: false,
              cursor,
              limit: 100,
            },
          },
        });
        if (error || !data) {
          throw new Error("Failed to load task activations");
        }
        rows.push(...data.task_activations);
        cursor = data.pagination.has_more
          ? (data.pagination.cursor ?? undefined)
          : undefined;
      } while (cursor);

      return rows;
    },
  });

  const assignments = useMemo<AssignmentLookup>(() => {
    if (planQuery.isLoading) {
      return { kind: "loading" };
    }
    // No activation means the plan has never been activated: every task is
    // unassigned (a ready, empty lookup) rather than loading forever.
    if (!latestActivation) {
      return { kind: "ready", byTaskId: new Map() };
    }
    if (assignmentsQuery.isLoading) {
      return { kind: "loading" };
    }
    if (assignmentsQuery.isError || !assignmentsQuery.data) {
      return { kind: "error" };
    }
    return {
      kind: "ready",
      byTaskId: buildAssignmentMap(assignmentsQuery.data),
    };
  }, [
    planQuery.isLoading,
    latestActivation,
    assignmentsQuery.isLoading,
    assignmentsQuery.isError,
    assignmentsQuery.data,
  ]);

  const { unassign, isPending: isUnassigning } = useUnassignTasks();
  const onUnassign = (taskId: string) =>
    unassign([taskId]).then(
      () => toast.success(t("page.taskPlanDetail.unassign.success")),
      (error: unknown) => {
        logger.warn("Task unassign failed", { taskId, error });
        toast.error(
          t(
            isConflict(error)
              ? "page.taskPlanDetail.unassign.conflict"
              : "page.taskPlanDetail.unassign.error",
          ),
        );
      },
    );

  useEffect(() => {
    const updatedState = nextState(state, planQuery, tasksQuery);

    if (typeof updatedState !== "undefined") {
      setState(updatedState);
    }
  }, [planQuery, tasksQuery, state, setState]);

  return (
    <div
      className="bg-surface h-full w-full"
      data-tenant-id={tenantId}
      data-org-id={orgId}
    >
      <TaskPlanDetailPage
        state={state}
        t={t}
        orgId={orgId}
        activation={activation}
        assignments={assignments}
        unassign={{ onUnassign, isPending: isUnassigning }}
        lang={lang}
        location={location}
      />
    </div>
  );
}
